import React from "react";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tag,
} from "@carbon/react";
import { TrashCan, Copy } from "@carbon/icons-react";
import i18next from "../../../i18n";
import { MaterialResponse, PurchaseRequestItemType } from "../../../types";
import { PurchaseRequestFilePayload } from "../../../types/purchaseRequestV2.type";
import { currencyFormatter } from "../../../utils";
import { FileReader } from "../../../components";
import ItemDetail from "./ItemDetail";
import type { ItemRow as ItemRowType } from "./types";

type SiteOption = { id: string; name: string };

export type ItemsTableProps = {
  items: ItemRowType[];
  fields: { clientId?: string }[];
  expandedId: string | null;
  onToggleExpand: (clientId: string) => void;
  sites: SiteOption[];
  todayString: string;

  /** The request-level defaults, used to grey out an item value still inherited from the request. */
  globalFrom: string | null;
  globalTo: string | null;
  globalBudgetValue: number | null;
  globalCurrency: string;

  onItemChange: (index: number, field: keyof ItemRowType, value: any) => void;
  onMaterialSelected: (index: number, material: MaterialResponse) => void;
  onClearMaterial: (index: number) => void;
  onCreateMaterial: (index: number, searchTerm: string) => void;
  onDuplicate: (index: number) => void;
  onRemove: (index: number) => void;
  uploadFile: (file: File) => Promise<PurchaseRequestFilePayload>;
  onUploadError?: (message: string) => void;
};

const COLUMN_COUNT = 9;

/** `2026-11-16` → `16/11/26`. Empty/partial values fall back to a dash. */
const formatShortDate = (value?: string | null) => {
  if (!value) return "";
  const [year, month, day] = value.split("-");
  return year && month && day ? `${day}/${month}/${year.slice(2)}` : value;
};

/** The item's needed date as a single day or a `from – to` range. */
const dateLabel = (item: ItemRowType) => {
  const from = formatShortDate(item.neededFrom);
  const to = formatShortDate(item.neededTo);
  if (!from && !to) return "-";
  if (!to || to === from) return from || to;
  return `${from} – ${to}`;
};

/**
 * Section 2 items table. One row per `useFieldArray` row; clicking a row expands `ItemDetail`
 * (the per-item editor) as a full-width `<tr>` spanning every column beneath it, driven by the
 * page's own `expandedId`.
 *
 * "Inherited from the request" rendering: a date or budget still equal to the request-level default
 * is drawn muted (greyed) — the requester hasn't overridden it — while an overridden value shows in
 * normal colour, so the grey vs. normal contrast tells the two apart at a glance (the helper line
 * under the table spells this out). A budget of none shows a dash.
 */
const ItemsTable: React.FC<ItemsTableProps> = ({
  items,
  fields,
  expandedId,
  onToggleExpand,
  sites,
  todayString,
  globalFrom,
  globalTo,
  globalBudgetValue,
  globalCurrency,
  onItemChange,
  onMaterialSelected,
  onClearMaterial,
  onCreateMaterial,
  onDuplicate,
  onRemove,
  uploadFile,
  onUploadError,
}) => {
  const headers = [
    "#",
    i18next.t("purchaseRequests.items.columns.type"),
    i18next.t("purchaseRequests.items.columns.description"),
    i18next.t("purchaseRequests.items.columns.quantity"),
    i18next.t("purchaseRequests.items.columns.measurementUnit"),
    i18next.t("purchaseRequests.items.columns.place"),
    i18next.t("purchaseRequests.items.columns.neededDate"),
    i18next.t("purchaseRequests.items.columns.budgetValue"),
    "",
  ];

  return (
    <Table className="new-request-table">
      <TableHead>
        <TableRow>
          {headers.map((header, i) => (
            <TableHeader key={i} id={`item-col-${i}`}>
              {header}
            </TableHeader>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((item, index) => {
          const clientId = item.clientId ?? `item-${index}`;
          const expanded = expandedId === clientId;
          const isService = item.itemType === PurchaseRequestItemType.SERVICE;

          const place = item.site?.id
            ? sites.find((s) => s.id === item.site?.id)?.name
            : item.address;

          const datesInherited =
            (item.neededFrom ?? null) === globalFrom &&
            (item.neededTo ?? null) === globalTo;

          const budgetInherited =
            item.budgetValue != null &&
            item.budgetValue === globalBudgetValue &&
            item.currency === globalCurrency;
          const budgetLabel =
            item.budgetValue == null
              ? "-"
              : currencyFormatter(
                  item.budgetValue,
                  item.currency ?? undefined
                ) || String(item.budgetValue);

          return (
            <React.Fragment key={fields[index]?.clientId ?? clientId}>
              <TableRow
                onClick={() => onToggleExpand(clientId)}
                aria-expanded={expanded}
                className="new-request-table__row"
              >
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <Tag type={isService ? "purple" : "blue"} size="sm">
                    {isService
                      ? i18next.t("purchaseRequests.items.typeService")
                      : i18next.t("purchaseRequests.items.typeGood")}
                  </Tag>
                </TableCell>
                <TableCell>
                  {isService ? (
                    <div className="new-request-table__service">
                      <span className="new-request-table__service-title">
                        {item.title ||
                          i18next.t(
                            "purchaseRequests.items.noMaterialPlaceholder"
                          )}
                      </span>
                      {item.description && (
                        <span className="new-request-table__service-desc">
                          {item.description}
                        </span>
                      )}
                      {item.brand && (
                        <span className="new-request-table__service-brand">
                          {i18next.t("purchaseRequests.items.brandLabel")}:{" "}
                          {item.brand}
                        </span>
                      )}
                      {(item.files ?? []).map((f, i) => (
                        <span
                          key={f.clientId ?? f.fileURL ?? `f-${i}`}
                          className="new-request-table__service-file"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <FileReader fileKey={f.fileURL} />
                          <span>{f.name || `file-${i + 1}`}</span>
                        </span>
                      ))}
                    </div>
                  ) : (
                    item.materialName ||
                    i18next.t("purchaseRequests.items.noMaterialPlaceholder")
                  )}
                </TableCell>
                <TableCell>{item.quantity ?? "-"}</TableCell>
                <TableCell>{item.measurementUnit || "-"}</TableCell>
                <TableCell>{place || "-"}</TableCell>
                <TableCell
                  className={
                    datesInherited ? "new-request-table__inherited" : undefined
                  }
                >
                  {dateLabel(item)}
                </TableCell>
                <TableCell
                  className={
                    budgetInherited ? "new-request-table__inherited" : undefined
                  }
                >
                  {budgetLabel}
                </TableCell>
                <TableCell className="new-request-table__actions">
                  <Button
                    kind="ghost"
                    size="sm"
                    hasIconOnly
                    renderIcon={Copy}
                    iconDescription={i18next.t(
                      "purchaseRequests.items.duplicate"
                    )}
                    tooltipPosition="left"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDuplicate(index);
                    }}
                  />
                  <Button
                    kind="ghost"
                    size="sm"
                    hasIconOnly
                    renderIcon={TrashCan}
                    iconDescription={i18next.t("cta.delete")}
                    tooltipPosition="left"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(index);
                    }}
                  />
                </TableCell>
              </TableRow>
              {expanded && (
                <TableRow className="new-request-table__detail-row">
                  <TableCell colSpan={COLUMN_COUNT}>
                    <ItemDetail
                      item={item}
                      index={index}
                      todayString={todayString}
                      sites={sites}
                      onItemChange={onItemChange}
                      onMaterialSelected={onMaterialSelected}
                      onClearMaterial={onClearMaterial}
                      onCreateMaterial={onCreateMaterial}
                      uploadFile={uploadFile}
                      onUploadError={onUploadError}
                    />
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default ItemsTable;
